import { User } from '@repo/domain/user';

import { Item, ItemType, ItemTypes, Mention } from './types';

export const afterSeparators = '[,.!?():;\'"`\\s]';
const beforeSeparators = '()\'"`\\s';

const mention = '@[a-zA-Z0-9]+';
const isMention = `^${mention}$`;

const afterOrEOL = `(?:(?=${afterSeparators})|(?=$))`;
/**
 * Finds mentions at the start of the string "(@aa) aa aa @aaa aa".
 **/
const startMentions = `(^${mention})${afterOrEOL}`;
/**
 * Finds a mention following a separator and groups everything
 * before the mention "@aa(( aa aa )(@aaa)) aa".
 **/
const mentionAfterText = `(?:(.*?[${beforeSeparators}])(${mention})${afterOrEOL})`;
/**
 * Groups everything else "@aa aa aa @aaa( aa)".
 **/
const plainText = `(.*)`;
/**
 * This regexp finds all mentions in the string and group all other text.
 **/
const match = new RegExp(
  `${startMentions}|${mentionAfterText}|${plainText}`,
  'gms'
);

/**
 * Splits the text into substrings, separating mentions and simple texts.
 */
export const splitByMentions = (text: string): string[] => {
  const groups = text.matchAll(match);
  return Array.from(groups).flatMap((group) => {
    // first element of group is matched text, we need only groups
    group.shift();
    return group.filter((x) => !!x);
  });
};

/**
 * Converts strings into mention or text items
 */
export const convertIntoItems = (textComponents: string[]): Item[] => {
  return textComponents.map((textComponent) => {
    if (!textComponent.match(isMention)) {
      return { type: 'text', value: textComponent };
    } else {
      return { type: 'mention', value: textComponent, mention: null };
    }
  });
};

/**
 * Tries to match all mention items into users, using searchMention method.
 * If the user is not found, then leave the unknown item.
 */
export const matchMentionItems = async (
  items: Item[],
  searchMention: (query: string) => Promise<Mention | null>
): Promise<Item[]> => {
  const newItems = [];

  for (const item of items) {
    if (item.type === 'text') {
      newItems.push(item);
      continue;
    }

    const result = await searchMention(item.value);
    if (result) {
      newItems.push({
        ...item,
        mention: result,
        value: `@${result.slug}`,
      });
      continue;
    }

    newItems.push(item);
  }
  return newItems;
};

/**
 * Returns the index of the item which contains the symbol in the position in plain text.
 * If item's type differs from param type, returns null.
 */
const findItemInPosition = (
  position: number,
  type: ItemTypes,
  items: Item[]
): number | null => {
  let itemEnd = 0;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    itemEnd += item.value.length;

    if (itemEnd >= position) {
      return type === 'any' || item.type === type ? i : null;
    }
  }
  return null;
};

/**
 * Returns the item which contains the symbol in the position in plain text.
 * If item's type differs from param type, returns null.
 */
export const getItemInPosition = <T extends ItemTypes>(
  position: number,
  type: T,
  items: Item[]
): ItemType<T> | null => {
  const itemIndex = findItemInPosition(position, type, items);

  return itemIndex !== null ? (items[itemIndex] as ItemType<T>) : null;
};

/**
 * Replace the item which contains the symbol in the position in plain text.
 */
export const updateItemInPosition = (
  position: number,
  items: Item[],
  newItem: Item
): Item[] => {
  const itemIndex = findItemInPosition(position, 'any', items);

  return items.map((x, i) => (i === itemIndex ? newItem : x));
};

/**
 * Finds the start or end symbol position of the item which contains the symbol
 * in the position in plain text.
 * If item's type differs from param type, returns null.
 */
export const findItemPositionBorder = (
  position: number,
  items: Item[],
  type: ItemTypes,
  border: 'start' | 'end'
): number | null => {
  const itemIndex = findItemInPosition(position, type, items);
  if (itemIndex === null) {
    return null;
  }

  const compareWithItem = (i: number): boolean =>
    border === 'start' ? i < itemIndex : i <= itemIndex;

  return items.reduce((sum, x, i) => {
    const length = x.value.length;
    const currentSum = compareWithItem(i) ? length : 0;
    return sum + currentSum;
  }, 0);
};

/**
 * Converts an array of items into a plain text.
 */
export const getText = (items: Item[]): string => {
  return items.map((i) => i.value).join('');
};

/**
 * Remove mention items with mention: null and merge them into surrounded text items
 */
export const mergeUnmatchedMentions = (items: Item[]): Item[] => {
  const newItems: Item[] = [];
  items.forEach((item) => {
    if (item.type === 'text' || !item.mention) {
      if (
        newItems.length > 0 &&
        newItems[newItems.length - 1].type === 'text'
      ) {
        newItems[newItems.length - 1].value = `${
          newItems[newItems.length - 1].value
        }${item.value}`;
      } else {
        newItems.push({ type: 'text', value: item.value });
      }
    } else {
      newItems.push(item);
    }
  });
  return newItems;
};

/**
 * Trim start of the first item and end of the last item
 */
export const trim = (items: Item[]): Item[] => {
  if (items.length === 1) {
    return [{ ...items[0], value: items[0].value.trim() }];
  }

  const newItems: Item[] = [];

  const trimmedStart = items[0].value.trimStart();
  if (trimmedStart) {
    newItems.push({ ...items[0], value: trimmedStart });
  }

  newItems.push(...items.slice(1, -1));

  const trimmedEnd = items[items.length - 1].value.trimEnd();
  if (trimmedEnd) {
    newItems.push({ ...items[items.length - 1], value: trimmedEnd });
  }

  return newItems;
};

export const convert = (user: User | null): Mention | null => {
  if (user && user.slug) {
    return {
      name: user.name,
      slug: user.slug,
      imageUrl: user.profileImage,
      isTeam: user.isTeam ?? false,
    };
  }
  return null;
};
